import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import noop from 'utils/noop';
import { EditableQuery } from '../types';
import { useQueryStateContext } from './queryState';

export type QueryHistoryStateContext = {
  undoQuery: () => void;
  redoQuery: () => void;
  undoStack: EditableQuery[];
  redoStack: EditableQuery[];
  addToUndoStack: () => void;
  resetRedoStack: () => void;
};

export type QueryHistoryStateContextProps = {
  passedUndoStack?: EditableQuery[];
  passedRedoStack?: EditableQuery[];
};

export const queryHistoryDefaultState: QueryHistoryStateContext = {
  undoQuery: noop,
  redoQuery: noop,
  addToUndoStack: noop,
  resetRedoStack: noop,
  undoStack: [],
  redoStack: [],
};

const QueryHistoryContext = createContext(queryHistoryDefaultState);

export const useQueryHistoryStateContext = () => useContext(QueryHistoryContext);

export const QueryHistoryContextProvider: FC<PropsWithChildren<QueryHistoryStateContextProps>> = ({
  passedUndoStack,
  passedRedoStack,
  children,
}) => {
  const [undoStack, setUndoStack] = useState<EditableQuery[]>(passedUndoStack ?? []);
  const [redoStack, setRedoStack] = useState<EditableQuery[]>(passedRedoStack ?? []);
  const { query, setQuery } = useQueryStateContext();

  /** add to undo stack when query state changes */
  const addToUndoStack = useCallback(() => {
    setUndoStack((prev) => {
      return prev.length !== 0 ? [...prev, query] : [query];
    });
  }, [query]);

  /** reset redo stack. This is applied when a new criteria, requirement, or condition is added */
  const resetRedoStack = useCallback(() => {
    setRedoStack([]);
  }, []);

  const undoQuery = useCallback(() => {
    if (undoStack.length !== 0) {
      const previousQuery = undoStack[undoStack.length - 1];
      setQuery(previousQuery);
      setRedoStack((prevRedoStack) => [query, ...prevRedoStack]);
      setUndoStack((prevUndoStack) =>
        prevUndoStack.length === undoStack.length
          ? prevUndoStack.slice(0, prevUndoStack.length - 1)
          : prevUndoStack,
      );
    }
  }, [query, undoStack, setUndoStack, setRedoStack]);

  const redoQuery = useCallback(() => {
    const nextQuery = redoStack[0];
    setQuery(nextQuery);
    setUndoStack((prevUndoStack) => [...prevUndoStack, query]);
    setRedoStack((prevRedoStack) =>
      prevRedoStack.length === redoStack.length
        ? prevRedoStack.slice(1, prevRedoStack.length)
        : prevRedoStack,
    );
  }, [query, redoStack, setUndoStack, setRedoStack]);

  const value = useMemo(
    () => ({
      undoQuery,
      redoQuery,
      addToUndoStack,
      resetRedoStack,
      undoStack,
      redoStack,
    }),
    [undoQuery, redoQuery, undoStack, redoStack, addToUndoStack, resetRedoStack],
  );
  return <QueryHistoryContext.Provider value={value}>{children}</QueryHistoryContext.Provider>;
};
